/**
 * Geolocation Error Message Helper
 *
 * Centralized error message mapping for geolocation errors.
 * Single source of truth for all GPS/geolocation error messages.
 *
 * @module utils/geolocation/errorMessages
 */

import type { TFunction } from 'i18next';
import type { GeolocationErrorCode } from '../../services/geolocation/geolocationService';

/**
 * Get internationalized error message for geolocation error code
 *
 * Maps geolocation error codes to user-friendly, localized error messages.
 * Eliminates duplicate error handling logic across components.
 *
 * @param errorCode - Geolocation error code from GeolocationService
 * @param t - i18next translation function
 * @returns Localized error message
 *
 * @example
 * ```typescript
 * const { t } = useTranslation();
 * const errorMessage = getGeolocationErrorMessage('PERMISSION_DENIED', t);
 * // Returns: "Dostęp do lokalizacji został zablokowany..." (PL)
 * //      or: "Location access has been blocked..." (EN)
 * ```
 */
export function getGeolocationErrorMessage(
  errorCode: GeolocationErrorCode,
  t: TFunction
): string {
  switch (errorCode) {
    case 'PERMISSION_DENIED':
      return t('map.locationPermissionDenied');

    case 'POSITION_UNAVAILABLE':
      return t('map.locationUnavailable');

    case 'TIMEOUT':
      return t('map.locationTimeout');

    case 'NOT_SUPPORTED':
      return t('gps.notSupported');

    default:
      // Fallback for unknown error codes
      return t('map.locationUnavailable');
  }
}
